import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { DateFilterValues } from '@components/Common/EntryFilters/DateFilterControlled';

// URL param keys
const PARAM_FROM = 'from';
const PARAM_TO = 'to';

export interface AdminFilters {
  startDate: string | null;
  endDate: string | null;
}

export interface UseAdminFiltersResult {
  filters: AdminFilters;
  dateFilter: DateFilterValues;
  setDateFilter: (value: DateFilterValues) => void;
  clearFilters: () => void;
}

/**
 * Hook to manage admin page filters via URL search params.
 *
 * URL format: /admin/flow-meters?from=xxx&to=xxx
 */
export function useAdminFilters(): UseAdminFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read values from URL
  const filters = useMemo<AdminFilters>(() => {
    const startDate = searchParams.get(PARAM_FROM);
    const endDate = searchParams.get(PARAM_TO);

    return {
      startDate,
      endDate,
    };
  }, [searchParams]);

  // Date filter for the controlled component
  const dateFilter = useMemo<DateFilterValues>(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
  }), [filters.startDate, filters.endDate]);

  // Update date filter in URL
  const setDateFilter = useCallback((value: DateFilterValues) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value.startDate) {
        newParams.set(PARAM_FROM, value.startDate);
      } else {
        newParams.delete(PARAM_FROM);
      }

      if (value.endDate) {
        newParams.set(PARAM_TO, value.endDate);
      } else {
        newParams.delete(PARAM_TO);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return {
    filters,
    dateFilter,
    setDateFilter,
    clearFilters,
  };
}

export default useAdminFilters;
